import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { UserRoundPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  convertToEnglishDigits,
  convertToPersianDigits,
} from "@/components/elements/numbers";
import { selectCurrentAuth } from "@/context/features/auth/authSlice";
import { useSelector } from "react-redux";
import { toaster } from "@/utils/toaster";
import { selectCurrentRoom } from "@/context/features/room/roomSlice";
import { cn } from "@/lib/utils";
import { CopyableLinkInput } from "@/components/ui/copyable-link-input";
import { motion, AnimatePresence } from "framer-motion";

const baseURL = import.meta.env.VITE_BIOARC_SERVICE || "https://bioarc.ir";

const IR_MOBILE_REGEX = /^(?:\+?98|0098|0)?9\d{9}$/;

const normalizeIranPhone = (input: string) => {
  const en = convertToEnglishDigits(input).replace(/\D/g, "");
  if (!IR_MOBILE_REGEX.test(en)) return null;
  let digits = en;
  if (digits.startsWith("0098")) digits = digits.slice(4);
  else if (digits.startsWith("98")) digits = digits.slice(2);
  if (!digits.startsWith("0")) digits = "0" + digits;
  return digits;
};

export default function Addpatient() {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [role, setRole] = useState("patient");
  const { user } = useSelector(selectCurrentAuth);
  const { focusedVid } = useSelector(selectCurrentRoom);
  const [gLink, setGLink] = useState<string>("");
  const sendInviteSMS = async () => {
    const normalized = normalizeIranPhone(phone);
    if (!normalized) {
      toaster("error", t("invalid_phone_number"));
      return;
    }
    // if (!focusedVid) {
    //   toaster("error", t("focus_on_patients"));
    //   return;
    // }
    try {
      const res = await fetch(
        `${baseURL}/VisitOnline/GenerateGuestLink?uid=${
          user?.id
        }&fullName=${encodeURIComponent(
          name
        )}&phoneNO=${normalized}&roleTag=${role}&vid=${focusedVid ?? ""}`
      );
      if (res.status === 200) {
        setPhoneError("");
        setPhone("");
        setName("");
        setRole("patient");
        const data = await res.json();
        if (data.link) setGLink(data.link);
        toaster("success", t("sms_sent"));
      } else {
        toaster("error", t("error_occurred"));
      }
    } catch {
      toaster("error", t("error_occurred"));
    }
  };
  useEffect(() => {
    setPhoneError("");

    const timeout = setTimeout(() => {
      if (phone) {
        if (!normalizeIranPhone(phone)) {
          setPhoneError(t("invalid_phone_number"));
        }
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [phone, t]);

  return (
    <Dialog
      open={showModal}
      // onOpenChange={setShowModal}
      onOpenChange={(open) => {
        if (!open) {
          setGLink("");
        }
        setShowModal(open);
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant={"outline"}
          className={"flex gap-1 items-center px-2 py-1 h-8"}
        >
          <UserRoundPlus className="mx-0 md:mx-1" size={18} />
          <span className="hidden md:block">{t("add_new_user")}</span>
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("add_person")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 ite">
          {/* {!focusedVid && (
            <div className="text-sm border rounded-md px-2 border-orange-500 bg-orange-500/10 w-fit self-center text-center p-1 shadow ">
              <span className=" text-orange-500  ">
                {t("focus_on_patients_for_add_new_user")}
              </span>
            </div>
          )} */}

          <div className="space-y-2">
            <Label className="text-sm  block">{t("full_name")}:</Label>
            <Input
              value={name}
              id="name"
              name="name"
              // disabled={!focusedVid}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("enter_full_name") + "."}
              className=""
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm  block">{t("phone_number")}:</Label>
            <Input
              inputMode="tel"
              id="tel"
              name="tel"
              autoComplete="true"
              // disabled={!focusedVid}
              value={convertToPersianDigits(phone)}
              onChange={(e) =>
                setPhone(
                  convertToEnglishDigits(e.target.value).replace(/[^\d+]/g, "")
                )
              }
              className={cn(
                phoneError ? "border-red-500 focus-visible:border-red-500" : ""
              )}
              placeholder={convertToPersianDigits("09123456789")}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm  block">{t("role")}:</Label>
            <Select
              value={role}
              onValueChange={setRole}
              //disabled={!focusedVid}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="">
                <SelectItem value="patient" className="">
                  {t("patient")}
                </SelectItem>
                <SelectItem value="physician" className="">
                  {t("physician")}
                </SelectItem>
                <SelectItem value="attendant" className="">
                  {t("attendant")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <AnimatePresence initial={false} mode="wait">
          {gLink && (
            <motion.div
              key="glink"
              initial={{ opacity: 0, y: 8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{
                type: "spring",
                stiffness: 420,
                damping: 30,
                mass: 0.6,
              }}
              className="space-y-4"
              role="region"
              aria-live="polite"
            >
              <CopyableLinkInput value={gLink} />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-5">
          <Button
            className="w-full font-medium rounded-md"
            onClick={sendInviteSMS}
            // disabled={!focusedVid}
          >
            {t("invite")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
